import React, { useEffect, useRef } from 'react';

interface MatrixOverlayProps {
  className?: string;
  resetTrigger?: number;
  // When true, keep classic green regardless of theme (used by Matrix Scan page)
  forceClassicGreen?: boolean;
}

export const MatrixOverlay: React.FC<MatrixOverlayProps> = ({ className, resetTrigger = 0, forceClassicGreen = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();

    const fontSize = 7.5; 
    const columns = Math.floor(canvas.width / fontSize);
    const drops: number[] = new Array(columns).fill(1);

    const matrix = '01';
    let frames = 0;

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [0, 255, 0];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [0, 255, 0];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    function draw() {
      const isLight = document.documentElement.getAttribute('data-theme') === 'light';
      // Use a softer light fade in light mode to avoid black background
      context.fillStyle = isLight ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)';
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Determine colors: header accent and text (theme-aware by default), classic green if forced
      let r = 0, g = 255, b = 0; // header accent default (green)
      let tr = 0, tg = 255, tb = 0; // text color default (green)
      if (!forceClassicGreen) {
        const styles = getComputedStyle(document.documentElement);
        const accent = styles.getPropertyValue('--color-header-accent').trim() || '#00ff00';
        const textColor = (styles.getPropertyValue('--color-text').trim() || styles.getPropertyValue('--text-color').trim() || accent);
        [r, g, b] = parseCssColorToRgb(accent);
        [tr, tg, tb] = parseCssColorToRgb(textColor);
      }
      context.fillStyle = `rgba(${r}, ${g}, ${b}, 1)`;
      context.font = `${fontSize}px monospace`;
      context.textAlign = 'center';

      for (let i = 0; i < drops.length; i++) {
        const text = matrix[Math.floor(Math.random() * matrix.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        const useTextColor = !forceClassicGreen && (i % 2 === 1);
        const cr = useTextColor ? tr : r;
        const cg = useTextColor ? tg : g;
        const cb = useTextColor ? tb : b;

        context.shadowBlur = 3;
        context.shadowColor = `rgba(${cr}, ${cg}, ${cb}, 1)`;
        
        const opacity = 1 - (y / canvas.height);
        context.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${Math.max(0.3, opacity)})`;
        
        context.fillText(text, x, y);
        
        context.shadowBlur = 0;

        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }

        drops[i]++;
      }

      frames++;
    }

    const interval = setInterval(draw, 50);
    window.addEventListener('resize', resizeCanvas);

    if (resetTrigger > 0) {
      drops.fill(1);
    }

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [resetTrigger]);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 w-full h-full pointer-events-none ${className || ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        opacity: 0.5,
      }}
    />
  );
};
